import {
  ContractSystemType,
  IngestionPlatform,
  UsState,
} from '@client/graphql/__generated__/types';
import * as Sentry from '@sentry/react';
import isNil from 'lodash/isNil';
import { useFeatureFlagPayload } from 'posthog-js/react';
import { z } from 'zod';

export enum FeatureKey {
  MAKE_OFFER = 'make-offer',
  OPEN_HOUSES = 'open-houses',
  OFFER_COACH = 'offer-coach',
  ORG_PIPELINE = 'org-pipeline',
  SYNC_KEY_TERMS_TO_DARWIN = 'sync-key-terms-to-darwin',
  KEY_TERMS_PDF = 'key-terms-pdf',
  DOTLOOP_COMPLIANCE_GROUP = 'dotloop-compliance-group',
  ESSENTIALS_PRODUCT = 'essentials-product',
  DATA_COACH_CHAT = 'data-coach-chat',
  ORG_COMPLIANCE_DASHBOARD = 'org-compliance-dashboard',
  BROKERSUMO_TRANSACTION_DATA = 'brokersumo-transaction-data',
  EXTENSION_REVIEW_QUEUES = 'extension-review-queues',
  EXTENSION_STALE_REJECTIONS = 'extension-stale-rejections',
  /**
   * Gates the extension's "Checklist complete" queue tab, which is piloting
   * with a single brokerage.
   *
   * Target it with an `organizations` payload ONLY. This hook ORs its
   * targeting dimensions, so adding `platforms: ['SKYSLOPE']` would open the
   * tab to every SkySlope brokerage rather than the pilot — and callers must
   * likewise pass `organizationId` alone, not `platform`.
   */
  CHECKLIST_COMPLETE_QUEUE = 'checklist-complete-queue',
  /**
   * Selects the REDESIGNED compliance dashboard. Distinct from
   * {@link ORG_COMPLIANCE_DASHBOARD}, which gates access to the dashboard at
   * all: someone can be entitled to the dashboard and still see the previous
   * one. Off (or still loading) means the previous dashboard.
   */
  ORG_COMPLIANCE_DASHBOARD_REDESIGN = 'org-compliance-dashboard-redesign',
  ORG_COMPLIANCE_CALIBRATION = 'org-compliance-calibration',
}

interface UseIsFeatureEnabledForPayloadProps {
  featureFlagKey: FeatureKey;
  mlsOSN?: string;
  state?: UsState;
  organizationId?: string;
  userEmail?: string;
  userId?: string;
  platform?: ContractSystemType | IngestionPlatform;
}

/**
 * Example payload from PostHog feature flag:
 * {
 *   state: [UsState.CA, UsState.TX],    // US states where the feature is enabled
 *   mls: ['CanopyMLS', 'TestMLS'],      // MLS originating system names
 *   // Ingestion and contract-system platforms
 *   platforms: ['SKYSLOPE', 'DOTLOOP', 'TRANSACTION_DESK'],
 *   // Exact addresses and/or whole-domain wildcards
 *   emails: ['someone@brokerage.com', '*@useindigo.com'],
 * }
 */

/**
 * The payload shape, parsed rather than asserted.
 *
 * These values are hand-typed into a PostHog text field, so they arrive from
 * outside the type system entirely: a `"emails": "me@x.com"` typo (a string
 * where an array belongs) would satisfy a cast and then throw inside
 * `matchesFeatureFlagEmail`, taking the whole page down with it.
 *
 * Each field is caught independently because the targeting dimensions are
 * independent: one malformed list should disable its own dimension, not silently
 * void the other five and hand every viewer a wrongly-gated page.
 */
const featureFlagPayloadSchema = z.object({
  emails: z.array(z.string()).optional().catch(undefined),
  enabledForEveryone: z.boolean().optional().catch(undefined),
  /**
   * Narrows a flag to Indigo staff on top of whatever else it targets, for a
   * phased rollout: ship to the pilot organization with this set, watch it,
   * then remove it to hand the feature to that organization's own users.
   *
   * Deliberately NOT read by {@link useIsFeatureEnabledForPayload} — that hook
   * ORs its dimensions, and this has to AND with them (targeted org AND
   * internal user), including taking precedence over `enabledForEveryone`.
   * A caller opting into it therefore reads the payload directly and combines
   * the two itself; see the extension's Checklist complete tab.
   */
  // `.catch(true)`, unlike every sibling above, because this field RESTRICTS
  // rather than targets. A malformed `emails` list simply matches nobody, which
  // is harmless; a malformed `internalOnly` that fell back to undefined would
  // drop the restriction and hand a piloting feature to the whole targeted
  // organization on the strength of a typo. Absent still means "no
  // restriction" — only a present-but-unparseable value fails closed.
  internalOnly: z.boolean().optional().catch(true),
  mls: z.array(z.string()).optional().catch(undefined),
  organizations: z.array(z.string()).optional().catch(undefined),
  platforms: z.array(z.string()).optional().catch(undefined),
  state: z.array(z.string()).optional().catch(undefined),
  users: z.array(z.string()).optional().catch(undefined),
});

/**
 * Every targeting list stays `string[]` — the shape the schema actually checked.
 * The enums ({@link UsState}, {@link ContractSystemType},
 * {@link IngestionPlatform}) are string enums, so callers still compare their
 * members against these lists directly, and an unrecognised value simply matches
 * nothing instead of failing the parse and voiding the whole dimension.
 *
 * `emails` holds exact addresses, or `*@domain` to match a whole domain. That
 * lets a feature be turned on for everyone at Indigo without listing each
 * person, which is what the `users` list would otherwise require.
 */
type FeatureFlagPayload = z.infer<typeof featureFlagPayloadSchema>;

/**
 * Whether `email` is covered by one payload entry.
 *
 * Two forms only: an exact address, or `*@domain` for every address at a domain.
 * Deliberately NOT a general glob — a pattern language in a feature-flag payload
 * is a way to enable a feature for more people than intended, and nobody has
 * asked for `*admin*@*`. An entry that is neither form matches nothing.
 *
 * Comparison is case-insensitive and trims surrounding whitespace, because these
 * values are typed into a PostHog text field by hand.
 */
export const matchesFeatureFlagEmail = (
  email: string,
  entry: string
): boolean => {
  const normalizedEmail = email.trim().toLowerCase();
  const normalizedEntry = entry.trim().toLowerCase();

  if (normalizedEmail === '' || normalizedEntry === '') {
    return false;
  }

  if (normalizedEntry.startsWith('*@')) {
    // Compare including the "@" so "*@indigo.com" cannot match
    // "someone@useindigo.com".
    return normalizedEmail.endsWith(normalizedEntry.slice(1));
  }

  return normalizedEmail === normalizedEntry;
};

export const useGetFeatureFlagPayload = (
  featureFlagKey: FeatureKey | undefined
): FeatureFlagPayload | undefined => {
  const payload = useFeatureFlagPayload(featureFlagKey ?? '');

  if (isNil(payload)) {
    return undefined;
  }

  const parsed = featureFlagPayloadSchema.safeParse(payload);

  if (!parsed.success) {
    // Only reachable when the payload is not an object at all; a bad individual
    // field is caught into `undefined` above. Reported rather than thrown: a
    // mistyped flag payload must not take down the page it gates, but it is a
    // misconfiguration someone has to see — silently ignoring it looks
    // identical to the flag simply being off.
    Sentry.captureMessage('Ignoring malformed feature flag payload', {
      extra: { featureFlagKey, issues: parsed.error.issues },
      level: 'warning',
    });

    return undefined;
  }

  return parsed.data;
};

export const useIsFeatureEnabledForPayload = ({
  featureFlagKey,
  ...rest
}: UseIsFeatureEnabledForPayloadProps) => {
  const payload = useGetFeatureFlagPayload(featureFlagKey);

  // make all features available in non-production environments
  if (process.env.NODE_ENV !== 'production') {
    return true;
  }

  if (!payload) {
    // ! TODO: Handle this with feature flag bootstrapping:
    // ! https://posthog.com/tutorials/bootstrap-feature-flags-react
    // payload is still loading
    return undefined;
  }

  if (payload?.enabledForEveryone) {
    return true;
  }

  if (Object.values(rest).every((value) => isNil(value))) {
    return false;
  }

  const { state, mlsOSN, organizationId, userEmail, userId, platform } = rest;

  if (state && payload?.state?.includes(state)) {
    return true;
  }

  if (platform && payload?.platforms?.includes(platform)) {
    return true;
  }

  if (mlsOSN && payload?.mls?.includes(mlsOSN)) {
    return true;
  }

  if (organizationId && payload?.organizations?.includes(organizationId)) {
    return true;
  }

  if (userId && payload?.users?.includes(userId)) {
    return true;
  }

  if (
    userEmail &&
    payload?.emails?.some((entry) => matchesFeatureFlagEmail(userEmail, entry))
  ) {
    return true;
  }

  return false;
};
